import React, { useState, useCallback, useEffect, useRef } from 'react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { Button, TextField, IconButton } from '@mui/material';
import { useAuth } from '../context/AuthContext';

// ─── Design tokens ────────────────────────────────────────────────────────────
// height: 34px ≈ 80 % of the Ask Anything input (~42 px)
// border-radius: 6px → slightly rounded rectangle

const tfSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    background: 'var(--bg-3)',
    transition: 'box-shadow 0.18s ease',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-2)' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px var(--green-glow)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--green-border)', borderWidth: '1px' },
    },
  },
  '& .MuiInputBase-input': {
    color: 'var(--text)',
    fontFamily: 'var(--font-tech)',
    fontSize: '0.83rem',
    fontWeight: 400,
    padding: '7px 12px',
    '&::placeholder': { color: 'var(--text-3)', opacity: 1 },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-3)',
    fontFamily: 'var(--font-tech)',
    fontSize: '0.83rem',
    fontWeight: 400,
    '&.Mui-focused': { color: 'var(--green)' },
  },
};

const btnContainedSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  background: 'var(--green)',
  color: 'var(--btn-text)',
  boxShadow: 'none',
  px: 1.75,
  '&:hover': { background: 'var(--green)', opacity: 0.88, boxShadow: '0 0 0 3px var(--green-glow)', transform: 'translateY(-1px)' },
  '&.Mui-disabled': { background: 'var(--green)', color: 'var(--btn-text)', opacity: 0.45 },
};

const btnOutlinedSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  border: '1px solid var(--border)',
  color: 'var(--text-2)',
  boxShadow: 'none',
  px: 1.75,
  '&:hover': { background: 'var(--bg-3)', borderColor: 'var(--border-2)', color: 'var(--text)', boxShadow: 'none' },
};

const btnTextGreenSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  color: 'var(--green)',
  minWidth: 0,
  px: 0.75,
  '&:hover': { background: 'var(--bg-3)' },
};

const btnDangerSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  color: 'var(--text-3)',
  minWidth: 0,
  px: 0.75,
  '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.08)' },
};

const iconBtnSx = {
  borderRadius: '6px',
  color: 'var(--text-3)',
  p: '5px',
  '&:hover': { color: 'var(--text)', background: 'var(--bg-3)' },
};

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

const COLOR_PRESETS = [
  '#c47aab', '#8b2e50', '#6d5a9c', '#3a9970',
  '#1c2c5e', '#b06a4f', '#7a8b6f', '#4a4a44',
];

function imgUrl(boardId, imageId) {
  return `${API_BASE}/api/boards/${boardId}/images/${imageId}/data`;
}

// ─── Board Create Page ────────────────────────────────────────────────────────

function BoardCreatePage({ onBack, onCreate, creating }) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor]             = useState(COLOR_PRESETS[0]);

  const submit = () => {
    if (!name.trim() || creating) return;
    onCreate({ name: name.trim(), description: description.trim() || null, color });
  };

  return (
    <div className="boards-page">
      <div className="boards-list-header">
        <div>
          <span className="section-label">Admin</span>
          <h2 className="boards-list-title">New Board</h2>
        </div>
        <Button
          startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={onBack}
          sx={btnOutlinedSx}
        >
          All Boards
        </Button>
      </div>

      <div className="boards-create-form">
        <TextField
          label="Board Name"
          size="small"
          fullWidth
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          sx={tfSx}
        />
        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional — describe what this board is for…"
          sx={{ ...tfSx, '& .MuiInputBase-input': { ...tfSx['& .MuiInputBase-input'], padding: '8px 12px' } }}
        />

        <div className="boards-create-color-row">
          <span className="boards-label">Accent color</span>
          <div className="boards-colors">
            {COLOR_PRESETS.map(c => (
              <button
                key={c}
                className={`boards-swatch ${color === c ? 'active' : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                title={c}
              />
            ))}
          </div>
        </div>

        <div className="boards-create-preview">
          <div className="boards-create-preview-band" style={{ background: color }} />
          <div className="boards-card-body" style={{ padding: '0.6rem 0.85rem' }}>
            <span className="boards-card-name">{name || 'Board Name'}</span>
            {description && <span className="boards-card-desc">{description}</span>}
          </div>
        </div>

        <div className="boards-create-footer">
          <Button onClick={onBack} sx={btnOutlinedSx}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={submit}
            disabled={!name.trim() || creating}
            sx={btnContainedSx}
          >
            {creating ? 'Creating…' : 'Create Board'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Boards component ────────────────────────────────────────────────────

export default function Boards() {
  const { token } = useAuth();
  const authHdrs = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [view, setView]               = useState('list'); // 'list' | 'create' | 'detail'
  const [boards, setBoards]           = useState([]);
  const [activeBoard, setActiveBoard] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [creating, setCreating]       = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [nameDraft, setNameDraft]     = useState('');
  const [descDraft, setDescDraft]     = useState('');

  const [uploading, setUploading]     = useState(false);
  const [dragging, setDragging]       = useState(false);
  const fileRef = useRef(null);

  const [editingCaption, setEditingCaption] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const loadBoards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/boards`);
      if (!res.ok) throw new Error(res.status);
      setBoards(await res.json());
    } catch {
      showToast('Could not load boards', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadBoard = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/boards/${id}`);
      if (!res.ok) throw new Error(res.status);
      setActiveBoard(await res.json());
    } catch {
      showToast('Could not load board', 'error');
    }
  }, [showToast]);

  useEffect(() => { loadBoards(); }, [loadBoards]);

  const openBoard = useCallback(async (id) => {
    await loadBoard(id);
    setView('detail');
  }, [loadBoard]);

  const goBack = useCallback(() => {
    setView('list');
    setActiveBoard(null);
    setEditingName(false);
    setEditingDesc(false);
    loadBoards();
  }, [loadBoards]);

  // ─── Board CRUD ───────────────────────────────────────────────────────────

  const handleCreate = useCallback(async (data) => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/boards`, {
        method: 'POST',
        headers: { ...authHdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(res.status);
      const board = await res.json();
      showToast(`"${board.name}" created`, 'success');
      setActiveBoard(board);
      setView('detail');
    } catch {
      showToast('Could not create board', 'error');
    } finally {
      setCreating(false);
    }
  }, [authHdrs, showToast]);

  const saveName = useCallback(async () => {
    if (!nameDraft.trim() || !activeBoard) return;
    try {
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}`, {
        method: 'PATCH',
        headers: { ...authHdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nameDraft.trim() }),
      });
      if (!res.ok) throw new Error(res.status);
      setActiveBoard(await res.json());
      setEditingName(false);
    } catch {
      showToast('Could not update name', 'error');
    }
  }, [nameDraft, activeBoard, authHdrs, showToast]);

  const saveDesc = useCallback(async () => {
    if (!activeBoard) return;
    try {
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}`, {
        method: 'PATCH',
        headers: { ...authHdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: descDraft.trim() || null }),
      });
      if (!res.ok) throw new Error(res.status);
      setActiveBoard(await res.json());
      setEditingDesc(false);
    } catch {
      showToast('Could not update description', 'error');
    }
  }, [descDraft, activeBoard, authHdrs, showToast]);

  const saveColor = useCallback(async (color) => {
    if (!activeBoard) return;
    try {
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}`, {
        method: 'PATCH',
        headers: { ...authHdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ color }),
      });
      if (!res.ok) throw new Error(res.status);
      setActiveBoard(await res.json());
    } catch {
      showToast('Could not update color', 'error');
    }
  }, [activeBoard, authHdrs, showToast]);

  const deleteBoard = useCallback(async () => {
    if (!activeBoard) return;
    if (!window.confirm(`Delete "${activeBoard.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}`, {
        method: 'DELETE',
        headers: authHdrs(),
      });
      if (!res.ok) throw new Error(res.status);
      showToast('Board deleted', 'success');
      goBack();
    } catch {
      showToast('Could not delete board', 'error');
    }
  }, [activeBoard, authHdrs, showToast, goBack]);

  // ─── Image CRUD ───────────────────────────────────────────────────────────

  const handleUpload = useCallback(async (files) => {
    if (!files?.length || !activeBoard) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) { showToast('Only image files are supported', 'error'); return; }
    if (file.size > 20 * 1024 * 1024) { showToast('Image must be under 20 MB', 'error'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}/images`, {
        method: 'POST',
        headers: authHdrs(),
        body: form,
      });
      if (!res.ok) throw new Error(res.status);
      showToast('Image uploaded', 'success');
      await loadBoard(activeBoard.id);
    } catch {
      showToast('Upload failed', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [activeBoard, authHdrs, showToast, loadBoard]);

  const deleteImage = useCallback(async (imageId) => {
    if (!activeBoard) return;
    try {
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}/images/${imageId}`, {
        method: 'DELETE',
        headers: authHdrs(),
      });
      if (!res.ok) throw new Error(res.status);
      setActiveBoard(prev => ({
        ...prev,
        images: prev.images.filter(i => i.id !== imageId),
        image_count: prev.image_count - 1,
      }));
    } catch {
      showToast('Could not delete image', 'error');
    }
  }, [activeBoard, authHdrs, showToast]);

  const saveImageCaption = useCallback(async () => {
    if (!editingCaption || !activeBoard) return;
    const { imageId, draft } = editingCaption;
    try {
      const res = await fetch(`${API_BASE}/api/boards/${activeBoard.id}/images/${imageId}`, {
        method: 'PATCH',
        headers: { ...authHdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: draft.trim() || null }),
      });
      if (!res.ok) throw new Error(res.status);
      const updated = await res.json();
      setActiveBoard(prev => ({
        ...prev,
        images: prev.images.map(i => i.id === imageId ? { ...i, caption: updated.caption } : i),
      }));
      setEditingCaption(null);
    } catch {
      showToast('Could not save caption', 'error');
    }
  }, [editingCaption, activeBoard, authHdrs, showToast]);

  const onDragOver  = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const onDragLeave = useCallback(() => setDragging(false), []);
  const onDrop      = useCallback((e) => { e.preventDefault(); setDragging(false); handleUpload(e.dataTransfer.files); }, [handleUpload]);

  const Toast = toast ? (
    <div className={`boards-toast boards-toast-${toast.type}`}>{toast.msg}</div>
  ) : null;

  // ─── Create view ──────────────────────────────────────────────────────────
  if (view === 'create') {
    return (
      <>
        {Toast}
        <BoardCreatePage
          onBack={goBack}
          onCreate={handleCreate}
          creating={creating}
        />
      </>
    );
  }

  // ─── Detail view ──────────────────────────────────────────────────────────
  if (view === 'detail' && activeBoard) {
    return (
      <div className="boards-page">
        {Toast}
        <div className="boards-detail-header" style={{ borderTopColor: activeBoard.color || '#c47aab' }}>
          <Button
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={goBack}
            sx={{ ...btnOutlinedSx, px: 1.25 }}
          >
            All Boards
          </Button>

          <div className="boards-detail-meta">
            {editingName ? (
              <div className="boards-inline-edit">
                <input
                  className="boards-name-input"
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                />
                <IconButton size="small" onClick={saveName} sx={iconBtnSx}><CheckRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => setEditingName(false)} sx={iconBtnSx}><CloseRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
              </div>
            ) : (
              <div className="boards-name-row" onClick={() => { setNameDraft(activeBoard.name); setEditingName(true); }}>
                <h2 className="boards-detail-title">{activeBoard.name}</h2>
                <span className="boards-edit-btn"><EditRoundedIcon sx={{ fontSize: 14 }} /></span>
              </div>
            )}

            {editingDesc ? (
              <div className="boards-inline-edit">
                <textarea
                  className="boards-desc-textarea"
                  value={descDraft}
                  onChange={e => setDescDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setEditingDesc(false); }}
                  rows={2}
                  autoFocus
                />
                <IconButton size="small" onClick={saveDesc} sx={iconBtnSx}><CheckRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => setEditingDesc(false)} sx={iconBtnSx}><CloseRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
              </div>
            ) : (
              <div className="boards-desc-row" onClick={() => { setDescDraft(activeBoard.description || ''); setEditingDesc(true); }}>
                <p className="boards-detail-desc">
                  {activeBoard.description || <span className="boards-placeholder">Add a description…</span>}
                </p>
                <span className="boards-edit-btn"><EditRoundedIcon sx={{ fontSize: 14 }} /></span>
              </div>
            )}

            <div className="boards-colors">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  className={`boards-swatch ${activeBoard.color === c ? 'active' : ''}`}
                  style={{ background: c }}
                  onClick={() => saveColor(c)}
                  title={c}
                />
              ))}
            </div>
          </div>

          <Button
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={deleteBoard}
            sx={btnDangerSx}
          >
            Delete Board
          </Button>
        </div>

        <div className="boards-image-grid">
          {activeBoard.images.map(img => (
            <div key={img.id} className="boards-image-item">
              <div className="boards-image-thumb-wrap">
                <img
                  src={imgUrl(activeBoard.id, img.id)}
                  alt={img.filename}
                  className="boards-image-thumb"
                  loading="lazy"
                />
                <button
                  className="boards-image-delete"
                  onClick={() => deleteImage(img.id)}
                  title="Remove"
                >
                  <CloseRoundedIcon sx={{ fontSize: 12 }} />
                </button>
              </div>
              <div className="boards-image-caption-row">
                {editingCaption?.imageId === img.id ? (
                  <input
                    className="boards-image-caption-input"
                    value={editingCaption.draft}
                    onChange={e => setEditingCaption(p => ({ ...p, draft: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveImageCaption();
                      if (e.key === 'Escape') setEditingCaption(null);
                    }}
                    onBlur={saveImageCaption}
                    autoFocus
                    placeholder="Add caption…"
                  />
                ) : (
                  <span
                    className="boards-image-caption-text"
                    onClick={() => setEditingCaption({ imageId: img.id, draft: img.caption || '' })}
                    title="Click to edit caption"
                  >
                    {img.caption || <em className="boards-placeholder">Add caption…</em>}
                  </span>
                )}
              </div>
            </div>
          ))}

          <div
            className={`boards-upload-zone ${dragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => !uploading && fileRef.current?.click()}
          >
            <CloudUploadRoundedIcon sx={{ fontSize: 18, color: 'var(--text-3)' }} />
            <span className="boards-upload-label">
              {uploading ? 'Uploading…' : 'Drop image or click'}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files)}
            />
          </div>
        </div>
      </div>
    );
  }

  // ─── List view ────────────────────────────────────────────────────────────
  return (
    <div className="boards-page">
      {Toast}

      <div className="boards-list-header">
        <div>
          <span className="section-label">Admin</span>
          <h2 className="boards-list-title">Boards</h2>
        </div>
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setView('create')}
          sx={btnTextGreenSx}
        >
          New Board
        </Button>
      </div>

      {loading ? (
        <div className="boards-empty">Loading boards…</div>
      ) : boards.length === 0 ? (
        <div className="boards-empty">
          <p>No boards yet.</p>
        </div>
      ) : (
        <div className="boards-grid">
          {boards.map(board => (
            <button key={board.id} className="boards-card" onClick={() => openBoard(board.id)}>
              <div className="boards-card-color" style={{ background: board.color || '#c47aab' }}>
                {board.cover_image_id && (
                  <img
                    src={imgUrl(board.id, board.cover_image_id)}
                    alt=""
                    className="boards-card-cover"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="boards-card-body">
                <span className="boards-card-name">{board.name}</span>
                {board.description && (
                  <span className="boards-card-desc">{board.description}</span>
                )}
                <span className="boards-card-count">
                  {board.image_count} {board.image_count === 1 ? 'image' : 'images'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
